import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import moment from 'moment'

// Selectors
import {
  selectCustomerIsInsuranceAgent,
  selectCustomerFirstName,
} from '../../../../store/selectors/customerProfile'
import {
  selectCurrentRisk,
  selectRiskYearsSince,
} from '../../../../store/selectors/homeInsurance'

// Constants
import { SYSTEM_REQUEST } from '../../../../constants/messages'
import { HOME_INSURANCE_RISKS } from '../../../../constants/homeInsurance'
import { HOME_INSURANCE_TOPIC_KEY } from '../../../../constants/topicKeys'

// Hooks
import { useHomeInsurance } from '../../../../hooks'

// Components
import { Page, If, Unless, Text, BotHeadSpin } from '../../..'
import { OverlayQuestionsFooter } from '../../../shared/Routing'
import { RedirectToHomeDigest } from '../../../shared/Navigation'
import RiskMessage from '../RiskMessage'
import GetHelpSection from './GetHelpSection'
import ConfirmOrSnoozeSection from './ConfirmOrSnoozeSection'

// Styles
import styles from './MoreDetails.module.scss'

const questionMessages = defineMessages({
  whatOptions: {
    id: 'Homeowner.marketSpotlight.questions.start',
    defaultMessage: 'Where should I start?',
  },
  qualifyRefi: {
    id: 'Homeowner.homeInsurance.questions.overpaying',
    defaultMessage: 'How do I know if I’m overpaying?',
  },
})

const MoreDetails = () => {
  const firstName = useSelector(selectCustomerFirstName)
  const insuranceAgentCustomerType = useSelector(selectCustomerIsInsuranceAgent)
  const risk = useSelector(selectCurrentRisk)
  const years = useSelector(selectRiskYearsSince)
  const { loading, shouldRender } = useHomeInsurance()
  const intl = useIntl()

  if (!loading && !shouldRender) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page>
      <Page.Body>
        <If condition={loading}>
          <BotHeadSpin panelSpacing />
        </If>
        <Unless condition={loading}>
          <div className={styles.header}>
            <Text
              size='xl'
              className={styles.title}
              variant='neutralCool'
              variantCount='900'
            >
              <If condition={insuranceAgentCustomerType}>
                <If condition={risk !== HOME_INSURANCE_RISKS.LOW}>
                  <FormattedMessage
                    id='Homeowner.homeInsurance.module.title'
                    defaultMessage='Have you done a home insurance checkup since {year}?'
                    values={{
                      year: moment().subtract(years, 'years').year(),
                    }}
                  />
                </If>
                <If condition={risk === HOME_INSURANCE_RISKS.LOW}>
                  <FormattedMessage
                    id='Homeowner.homeInsurance.module.title.lowRisk'
                    defaultMessage='Looks like you last reviewed your policy in {year}'
                    values={{
                      year: moment().subtract(years, 'years').year(),
                    }}
                  />
                </If>
              </If>
              <If condition={!insuranceAgentCustomerType}>
                <FormattedMessage
                  id='Homeowner.homeInsurance.panelTitle'
                  defaultMessage='Your risk of overpaying or being underinsured is {risk}'
                  values={{
                    risk: <RiskMessage risk={risk} />,
                  }}
                />
              </If>
            </Text>
            <Text variant='neutralCool' variantCount='700'>
              <FormattedMessage
                id='Homeowner.homeInsurance.panelTitleSub'
                defaultMessage='Insurance policies commonly increase each year. Plus, if you’ve made home updates, you could miss out on savings or adequate coverage.'
              />
            </Text>
          </div>
          <GetHelpSection />
          <ConfirmOrSnoozeSection />
        </Unless>
      </Page.Body>
      <Page.Footer>
        <OverlayQuestionsFooter
          topic={intl.formatMessage(SYSTEM_REQUEST.homeInsuranceTopic)}
          topicKey={HOME_INSURANCE_TOPIC_KEY}
          questions={Object.values(questionMessages)}
          placeholder
          disableCospo
          renderHeader={() => (
            <>
              <Text
                className={styles.footerHeader}
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='HomeInsurance.footer.main'
                  defaultMessage='Ready to take the next step but not sure where to start?'
                />
              </Text>
              <Text size='xs' variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='HomeInsurance.footer.sub'
                  defaultMessage='{firstName} is happy to point you in the right direction.'
                  values={{ firstName }}
                />
              </Text>
            </>
          )}
        />
      </Page.Footer>
    </Page>
  )
}

export default MoreDetails
